// client/src/components/layout/Sidebar.js - VERSÃO FINAL OTIMIZADA
import React, { useMemo, useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/auth/AuthProvider';
import useContaPlan from '../../hooks/useContaPlan';
import { usePlataformaBranding } from '../../hooks/usePlataformaBranding';
import packageJson from '../../../package.json';
import {
    // Ícones Originais:
    LayoutDashboard, Users, Building, FileText, Settings, UsersRound,
    LogOut, CalendarDays, ChevronDown, FolderKanban, ShieldCheck, Briefcase,
    User as UserIcon, ClipboardList, MessageSquare, Receipt, Award, Clock, CalendarCheck,
    Archive, ShoppingCart, FileSignature, GalleryHorizontal, Package, CreditCard, Home,
    BarChart2, DollarSign, Globe, Phone, HelpCircle, Share2, Image, Gavel, ListTree, MessageSquareQuote,
    Cake, Percent, CalendarClock, MessageCircle, TrendingUp,
    // Ícones adicionados:
    Gift, Tag, Loader2, Bell, Shield, Database, BarChart3, Users2,
    // Novos ícones para permissões:
    Key, Lock, Unlock, Eye, EyeOff, Edit, Trash2, Plus, Filter,
    // Ícones para novas páginas:
    MapPin, BriefcaseBusiness, UserCog, Truck, GraduationCap, Mail, Compass,
    // Retração da sidebar (desktop):
    PanelLeftClose, PanelLeftOpen,
    Banknote, Coins, LogIn
} from 'lucide-react';
import { 
    hasPermission, 
    SUBSCRIPTION_PERMISSIONS,
    getUserScope,
    isMatrizAdmin,
    isFranquiaAdmin,
    isContaAdmin,
    canView,
    canAccessRoute,
    debugPermissions 
} from '../../utils/permissions';

// --- Componente NavLink ---
const NavLink = ({ to, label, icon, toggleSidebar, badge }) => {
    const location = useLocation();
    const isActive = location.pathname === to ||
                     (to !== "/" && location.pathname.startsWith(to) && location.pathname.charAt(to.length) === '/');
    const isHomeActive = to === "/" && location.pathname === "/";
    const finalIsActive = isHomeActive || (!isHomeActive && isActive);

    return (
        <li>
            <Link
                to={to}
                onClick={toggleSidebar}
                className={`sidebar-nav-link ${finalIsActive ? 'sidebar-nav-link-active' : ''}`}
                aria-current={finalIsActive ? 'page' : undefined}
                title={label}
            >
                {React.cloneElement(icon, { className: 'sidebar-nav-icon' })}
                <span className="flex-1">{label}</span>
                {badge && (
                    <span className="sidebar-badge">
                        {badge}
                    </span>
                )}
            </Link>
        </li>
    );
};

// --- Componente SubMenu ---
const SubMenu = ({ item, toggleSidebar }) => {
    const location = useLocation();
    const isParentActive = useMemo(() =>
        item.subItems?.some(sub => location.pathname.startsWith(sub.to)) || false,
        [item.subItems, location.pathname]
    );
    const [isOpen, setIsOpen] = useState(isParentActive);

    useEffect(() => {
        if (isParentActive && !isOpen) {
            setIsOpen(true);
        }
    }, [isParentActive, isOpen]);

    if (!item.subItems || item.subItems.length === 0) {
        return null;
    }

    return (
        <li className="sidebar-submenu">
            <button
                type="button"
                className={`sidebar-nav-link ${isParentActive ? 'sidebar-submenu-active' : ''}`}
                onClick={() => setIsOpen(!isOpen)}
                aria-expanded={isOpen}
                title={item.label}
            >
                {React.cloneElement(item.icon, { className: 'sidebar-nav-icon' })}
                <span className="flex-1">{item.label}</span>
                <ChevronDown size={16} className={`ml-auto transition-transform sidebar-submenu-chevron ${isOpen ? 'rotate-180' : ''}`} />
            </button>
            {isOpen && (
                <ul className="sidebar-submenu-list">
                    {item.subItems.map(subItem => (
                        <NavLink key={subItem.to} {...subItem} toggleSidebar={toggleSidebar} />
                    ))}
                </ul>
            )}
        </li>
    );
};

// --- Componente Principal Sidebar ---
const Sidebar = ({ isOpen, toggleSidebar, isCollapsed, toggleCollapse }) => {
    const { user, logout, isLoading } = useAuth();
    const { hasFeature } = useContaPlan();
    // ✅ GET é liberado pra qualquer usuário logado (ver plataformaBrandingRoutes.js)
    // — sidebar renderiza a mesma logo/fallback em qualquer tenant.
    const { data: branding } = usePlataformaBranding();
    const onLogout = () => { if (logout) logout(); };

    // Controle de logs para evitar repetição
    const hasLoggedInitialDebug = React.useRef(false);

    // DEBUG: Mostrar estrutura do usuário (uma vez por sessão)
    useEffect(() => {
        if (user && !hasLoggedInitialDebug.current) {
            console.log('🔍 Sidebar - Estrutura do usuário:', debugPermissions(user));
            
            // Verificar acesso específico (apenas uma vez)
            const routesToCheck = [
                '/franquias',
                '/plataforma/contas',
                '/plataforma/planos-contas',
                '/plataforma/planos-franquias'
            ];
            
            routesToCheck.forEach(route => {
                const hasAccess = canAccessRoute(user, route);
                console.log(`🔐 Acesso à ${route}: ${hasAccess ? '✅ PERMITIDO' : '❌ NEGADO'}`);
            });
            
            hasLoggedInitialDebug.current = true;
        }
    }, [user]);

    const navItems = useMemo(() => {
        if (isLoading || !user) return [];

        const menu = [];
        
        // ✅ USAR AS FUNÇÕES CORRETAS DO permissions.js
        const userScope = getUserScope(user);
        const isMatrizUser = isMatrizAdmin(user);
        const isFranquiaUser = isFranquiaAdmin(user);
        const isContaUser = isContaAdmin(user);
        const isCliente = userScope === 'cliente';

        // DEBUG log detalhado (apenas uma vez)
        const debugKey = `sidebar_debug_${user.email}`;
        if (process.env.NODE_ENV === 'development' && !sessionStorage.getItem(debugKey)) {
            console.log('🎯 Sidebar - Verificações CRÍTICAS:', {
                userEmail: user.email,
                roleName: user.role?.name,
                roleScope: user.role?.scope,
                userScope: userScope,
                isMatrizAdmin: isMatrizUser,
                isFranquiaAdmin: isFranquiaUser,
                isContaAdmin: isContaUser,
                isCliente: isCliente,
                hasMatrizFullAccess: (user.permissions || user.role?.permissions || []).includes('matriz_admin:full_access')
            });
            sessionStorage.setItem(debugKey, 'true');
        }

        // 1. DASHBOARD
        if (hasPermission(user, 'dashboard:view')) {
            menu.push({ to: "/", label: "Dashboard", icon: <LayoutDashboard size={18} /> });
        }

        // 2. MENU MATRIZ - USAR A VERIFICAÇÃO CORRETA
        if (isMatrizUser) {
            const matrizSubItems = [];
            
            // Verificar cada item com canAccessRoute
            const matrizItems = [
                { 
                    to: "/franquias", 
                    label: "Franquias", 
                    icon: <Home size={18} />,
                    check: () => canAccessRoute(user, '/franquias')
                },
                { 
                    to: "/plataforma/contas", 
                    label: "Contas", 
                    icon: <Building size={18} />,
                    check: () => canAccessRoute(user, '/plataforma/contas')
                },
                { 
                    to: "/plataforma/planos-contas", 
                    label: "Planos de Contas", 
                    icon: <Package size={18} />,
                    check: () => canAccessRoute(user, '/plataforma/planos-contas')
                },
                { 
                    to: "/plataforma/planos-franquias", 
                    label: "Planos de Franquia", 
                    icon: <Briefcase size={18} />,
                    check: () => canAccessRoute(user, '/plataforma/planos-franquias')
                },
                {
                    to: "/galeria/gerenciamento",
                    label: "Galeria Global",
                    icon: <GalleryHorizontal size={18} />,
                    check: () => canAccessRoute(user, '/galeria/gerenciamento')
                },
                {
                    to: "/plataforma/catalogo-modelo",
                    label: "Catálogo de Modelos",
                    icon: <Package size={18} />,
                    check: () => canAccessRoute(user, '/plataforma/catalogo-modelo')
                },
                {
                    to: "/plataforma/permissoes",
                    label: "Permissões",
                    icon: <Key size={18} />,
                    check: () => hasPermission(user, 'permissoes:manage')
                },
                {
                    to: "/plataforma/notas-fiscais",
                    label: "Notas Fiscais (Mensalidade)",
                    icon: <Receipt size={18} />,
                    check: () => hasPermission(user, 'matriz_nota_fiscal:view')
                },
            ];
            
            matrizItems.forEach(item => {
                if (item.check()) {
                    matrizSubItems.push({ to: item.to, label: item.label, icon: item.icon });
                }
            });
            
            if (matrizSubItems.length > 0) {
                menu.push({ 
                    type: 'submenu', 
                    label: 'Administração', 
                    icon: <ShieldCheck size={18}/>, 
                    subItems: matrizSubItems 
                });
            }
            
            // SITE INSTITUCIONAL - Apenas Matriz
            menu.push({ 
                type: 'submenu', 
                label: 'Site Institucional', 
                icon: <Globe size={18} />, 
                subItems: [
                    { to: "/site-institucional/paginas", label: "Páginas", icon: <FileText size={18} /> },
                    { to: "/site-institucional/conteudos-home", label: "Conteúdos Seções", icon: <ListTree size={18} /> },
                    { to: "/site-institucional/banners-home", label: "Banners", icon: <Image size={18} /> },
                    { to: "/site-institucional/faq", label: "FAQ", icon: <HelpCircle size={18} /> },
                    { to: "/site-institucional/redes-sociais", label: "Redes Sociais", icon: <Share2 size={18} /> },
                    { to: "/site-institucional/navegacao", label: "Navegação", icon: <Compass size={18} /> },
                    { to: "/site-institucional/contatos", label: "Contatos", icon: <Phone size={18} /> },
                    { to: "/site-institucional/mensagens", label: "Mensagens de Contato", icon: <Mail size={18} /> },
                    { to: "/site-institucional/depoimentos", label: "Depoimentos", icon: <MessageSquareQuote size={18} /> },
                    { to: "/site-institucional/configuracoes-home", label: "Configurações Home", icon: <Settings size={18} /> },
                ]
            });
            
            menu.push({
                type: 'submenu',
                label: 'Termos e Políticas',
                icon: <Gavel size={18} />,
                subItems: [
                    { to: "/site-institucional/politicas", label: "Gerenciar Documentos", icon: <FileSignature size={18} /> },
                ]
            });

            // ✅ Comunicação da Matriz — mesmas telas de Conta/Franquia
            // (ComunicacaoAutomaticaPage.js e ComunicacaoManualPage.js já são
            // scope-aware: isMatrizAdmin habilita o domínio 'Sistema', usado
            // pelos eventos da própria plataforma como "esqueci_senha" — sem
            // uma config automática aqui, recuperação de senha por
            // WhatsApp/SMS/e-mail nunca tem canal definido e sempre falha).
            // Backend já autoriza matriz em todas as rotas de
            // comunicacaoConfigRoutes.js — só faltava o item no menu.
            if (hasPermission(user, 'comunicacao_configs:view')) {
                menu.push({
                    type: 'submenu',
                    label: 'Comunicação',
                    icon: <MessageSquare size={18} />,
                    subItems: [
                        { to: "/configuracoes/comunicacao/automatica", label: "Comunicação Automática", icon: <MessageSquare size={18} /> },
                        { to: "/configuracoes/comunicacao/manual", label: "Comunicação Manual", icon: <MessageCircle size={18} /> },
                    ]
                });
            }
        }

        // 3. MENU GESTÃO FRANQUIA (scope franquia)
        if (isFranquiaUser && user.franquia) {
            const franquiaSubItems = [];
            
            if (hasPermission(user, 'contas:view')) {
                franquiaSubItems.push({ 
                    to: `/franquias/${user.franquia._id || user.franquia}/contas`, 
                    label: "Contas", 
                    icon: <Building size={18} /> 
                });
            }
            
            if (hasPermission(user, SUBSCRIPTION_PERMISSIONS.FRANQUIA)) {
                franquiaSubItems.push({ 
                    to: "/assinatura", 
                    label: "Assinatura Franquia", 
                    icon: <CreditCard size={18} /> 
                });
            }
            
            // "Relatórios" (/relatorios/franquia) removido — não existe
            // rota nenhuma registrada pra esse caminho em App.js, o link
            // sempre levava a uma página em branco/erro. Reintroduzir só
            // quando houver de fato uma tela de relatórios agregados por
            // franquia implementada.

            if (franquiaSubItems.length > 0) {
                menu.push({ 
                    type: 'submenu', 
                    label: 'Gestão da Franquia', 
                    icon: <Briefcase size={18}/>, 
                    subItems: franquiaSubItems 
                });
            }
        }

        // 4. MENUS GERAIS (operação do dia a dia de UMA conta — agenda,
        // vendas, pedidos, disponibilidade). Antes usava `!isMatrizUser &&
        // !isCliente`, que também deixava passar franquia (nem matriz nem
        // cliente) — só que franquia não tem user.account (opera várias
        // contas, não uma só), então essas páginas apareciam na sidebar e
        // quebravam/ficavam sem dados. Mesmo padrão de guarda já usado nas
        // seções de Horários/Financeiro logo abaixo (`!isMatrizUser &&
        // user.account`).
        if (userScope === 'conta' && user.account) {
            if (hasPermission(user, 'agenda:view') && hasFeature('hasAgenda')) {
                menu.push({
                    to: "/agendamentos",
                    label: "Agendamentos",
                    icon: <CalendarDays size={18} />
                });
            }

            if (hasPermission(user, 'vendas:view') && hasFeature('hasVendas')) {
                menu.push({
                    to: "/vendas",
                    label: "Vendas",
                    icon: <TrendingUp size={18} />
                });
            }

            if (hasPermission(user, 'pedidos:view') && hasFeature('hasPedidos')) {
                menu.push({
                    to: "/pedidos",
                    label: "Pedidos",
                    icon: <Package size={18} />
                });
            }

            // ✅ hasFeature: as 3 abaixo agora são features de plano
            // (hasFidelidade/hasListaEspera/hasNotaFiscal em ContasPlan) —
            // antes ficavam liberadas em qualquer plano, só permissão de
            // usuário controlava.
            if (hasPermission(user, 'fidelidade:view') && hasFeature('hasFidelidade')) {
                menu.push({
                    to: "/fidelidade",
                    label: "Fidelidade",
                    icon: <Gift size={18} />
                });
            }

            if (hasPermission(user, 'lista_espera:view') && hasFeature('hasListaEspera')) {
                menu.push({
                    to: "/lista-espera",
                    label: "Lista de Espera",
                    icon: <Clock size={18} />
                });
            }

            if (hasPermission(user, 'nota_fiscal:view') && hasFeature('hasNotaFiscal')) {
                menu.push({
                    to: "/nota-fiscal",
                    label: "Notas Fiscais",
                    icon: <Receipt size={18} />
                });
            }

            if (hasPermission(user, 'agenda:view') && hasFeature('hasAgenda')) {
                menu.push({
                    to: "/configuracoes/disponibilidade",
                    label: "Disponibilidade",
                    icon: <CalendarCheck size={18} />
                });
            }
        }

        // 5. MENU CADASTROS (scope conta ou franquia com conta)
        if (user.account && (isContaUser || isFranquiaUser)) {
            const cadastrosSubItems = [];

            if (canView(user, 'clientes')) {
                cadastrosSubItems.push({ to: "/cadastros/clientes", label: "Clientes", icon: <Users size={18} /> });
            }
            if (hasPermission(user, 'categorias:view') && hasFeature('hasCategorias')) {
                cadastrosSubItems.push({ to: "/cadastros/categorias", label: "Categorias", icon: <Tag size={18} /> });
            }
            if (hasPermission(user, 'fornecedores:view')) {
                cadastrosSubItems.push({ to: "/cadastros/fornecedores", label: "Fornecedores", icon: <Building size={18} /> });
            }
            if (hasPermission(user, 'transportadores:view')) {
                cadastrosSubItems.push({ to: "/cadastros/transportadores", label: "Transportadores", icon: <Truck size={18} /> });
            }
            if (hasPermission(user, 'recursos:view') && hasFeature('hasRecursos')) {
                cadastrosSubItems.push({ to: "/cadastros/recursos", label: "Recursos", icon: <UsersRound size={18} /> });
            }
            if (hasPermission(user, 'servicos:view') && hasFeature('hasServicos')) {
                cadastrosSubItems.push({ to: "/cadastros/servicos", label: "Serviços", icon: <ClipboardList size={18} /> });
            }
            if (hasPermission(user, 'itens_venda:view') && hasFeature('hasItensVenda')) {
                cadastrosSubItems.push({ to: "/cadastros/itens-venda", label: "Itens de Venda", icon: <ShoppingCart size={18} /> });
            }
            if (hasPermission(user, 'catalogo_modelo:view')) {
                cadastrosSubItems.push({ to: "/cadastros/catalogo-modelo", label: "Catálogo Onmicare", icon: <Package size={18} /> });
            }
            if (hasPermission(user, 'ofertas:view') && hasFeature('hasOfertas')) {
                cadastrosSubItems.push({ to: "/cadastros/ofertas", label: "Ofertas", icon: <Gift size={18} /> });
            }
            if (hasPermission(user, 'cursos:view') && hasFeature('hasCursos')) {
                cadastrosSubItems.push({ to: "/cadastros/cursos", label: "Cursos", icon: <GraduationCap size={18} /> });
            }
            if (hasPermission(user, 'pacotes_cliente:view') && hasFeature('hasPacotesClientes')) {
                cadastrosSubItems.push({ to: "/cadastros/pacotes-cliente", label: "Pacotes Vendidos", icon: <Archive size={18} /> });
            }

            if (cadastrosSubItems.length > 0) {
                menu.push({ type: 'submenu', label: 'Cadastros', icon: <FolderKanban size={18} />, subItems: cadastrosSubItems });
            }
        }

        // 6. MENU CONFIGURAÇÕES
        const configuracoesSubItems = [];
        
        if (!isMatrizUser && user.account && hasPermission(user, 'horarios:manage')) {
            configuracoesSubItems.push({ 
                to: "/configuracoes/horarios",
                label: "Horários", 
                icon: <Clock size={18} /> 
            });
        }
        
        if (isMatrizUser || (user.account && hasPermission(user, 'metodos_pagamento:manage'))) {
            configuracoesSubItems.push({
                to: "/configuracoes/pagamentos",
                label: "Formas de Pagamento",
                icon: <CreditCard size={18} />
            });
        }

        if (isMatrizUser || (user.account && hasPermission(user, 'sinais:view'))) {
            configuracoesSubItems.push({
                to: "/configuracoes/sinais",
                label: "Regras de Sinal",
                icon: <Banknote size={18} />
            });
        }

        if (!isMatrizUser && user.account) {
            configuracoesSubItems.push({
                to: "/configuracoes/entrega-retirada",
                label: "Entrega e Retirada",
                icon: <Truck size={18} />
            });
        }
        
        if (!isMatrizUser && user.account && hasPermission(user, 'financeiro:view')) {
            configuracoesSubItems.push({
                to: "/configuracoes/financeiro",
                label: "Financeiro",
                icon: <DollarSign size={18} />
            });
        }

        if (!isMatrizUser && user.account && hasPermission(user, 'fidelidade:manage') && hasFeature('hasFidelidade')) {
            configuracoesSubItems.push({
                to: "/configuracoes/fidelidade",
                label: "Fidelidade",
                icon: <Coins size={18} />
            });
        }

        if (!isMatrizUser && user.account && hasPermission(user, 'lista_espera:manage') && hasFeature('hasListaEspera')) {
            configuracoesSubItems.push({
                to: "/configuracoes/lista-espera",
                label: "Lista de Espera",
                icon: <Clock size={18} />
            });
        }

        if (!isMatrizUser && user.account && hasPermission(user, 'nota_fiscal:manage') && hasFeature('hasNotaFiscal')) {
            configuracoesSubItems.push({
                to: "/configuracoes/nota-fiscal",
                label: "Nota Fiscal",
                icon: <Receipt size={18} />
            });
        }
        
        if ((isMatrizUser || user.account) && hasPermission(user, 'comunicacao_configs:view')) {
            configuracoesSubItems.push({
                to: "/configuracoes/integracoes",
                label: "Integrações",
                icon: <Key size={18} />
            });
        }

        // ✅ Acesso de Suporte (impersonate por concessão) — só Franquia/Conta
        // CONCEDEM acesso (é o próprio tenant liberando); Matriz nunca vê
        // esse item porque é sempre quem USA o impersonate, não quem libera.
        if (!isMatrizUser && (user.account || user.franquia) && hasPermission(user, 'seguranca:gerenciar_acesso_suporte')) {
            configuracoesSubItems.push({
                to: "/configuracoes/acesso-suporte",
                label: "Acesso de Suporte",
                icon: <ShieldCheck size={18} />
            });
        }

        // ✅ Administração de pacotes de Créditos de Comunicação — só Matriz
        // (é quem vende os créditos que Conta compra em Integrações).
        if (isMatrizUser && hasPermission(user, 'comunicacao_configs:edit')) {
            configuracoesSubItems.push({
                to: "/configuracoes/creditos-comunicacao",
                label: "Créditos de Comunicação",
                icon: <Coins size={18} />
            });
        }

        // ✅ Nota fiscal da mensalidade SaaS — só Matriz (é quem emite,
        // cobrando Franquia/Conta pela plataforma).
        if (isMatrizUser && hasPermission(user, 'matriz_nota_fiscal:manage')) {
            configuracoesSubItems.push({
                to: "/configuracoes/nota-fiscal-plataforma",
                label: "Nota Fiscal da Mensalidade",
                icon: <Receipt size={18} />
            });
        }

        // ✅ Logo da plataforma — só Matriz (é a identidade visual exibida
        // na sidebar/drawer de TODOS os tenants, não uma logo de tenant).
        if (isMatrizUser && hasPermission(user, 'matriz_branding:manage')) {
            configuracoesSubItems.push({
                to: "/configuracoes/branding-plataforma",
                label: "Logo da Plataforma",
                icon: <Image size={18} />
            });
        }

        // ✅ Google Meu Negócio — credenciais OAuth da plataforma, só Matriz
        // configura; quem conecta de verdade é cada Conta, pelo app mobile.
        if (isMatrizUser && hasPermission(user, 'matriz_google_negocio:manage')) {
            configuracoesSubItems.push({
                to: "/configuracoes/google-negocio",
                label: "Google Meu Negócio",
                icon: <Building size={18} />
            });
        }

        // ✅ Login com Google — Client ID pro cliente final (site/conta);
        // libera por Conta via feature de plano (hasLoginGoogle), não aqui.
        if (isMatrizUser && hasPermission(user, 'matriz_google_login:manage')) {
            configuracoesSubItems.push({
                to: "/configuracoes/google-login",
                label: "Login com Google",
                icon: <LogIn size={18} />
            });
        }

        if (configuracoesSubItems.length > 0) {
            menu.push({ 
                type: 'submenu', 
                label: 'Configurações', 
                icon: <Settings size={18} />, 
                subItems: configuracoesSubItems 
            });
        }

        // 7. MENU DOCUMENTOS — documentos gerados/preenchidos (não confundir
        // com Templates, que são as definições/modelos usados pra gerá-los).
        {
            const documentosSubItems = [];

            if (hasPermission(user, 'clientes:view')) {
                documentosSubItems.push({ to: "/documentos/certificados", label: "Certificados", icon: <Award size={18} /> });
            }
            if (hasPermission(user, 'contratos:view')) {
                documentosSubItems.push({ to: "/contratos", label: "Contratos", icon: <FileSignature size={18} /> });
            }
            if (hasPermission(user, 'recibos:view')) {
                documentosSubItems.push({ to: "/documentos/recibos", label: "Recibos", icon: <Receipt size={18} /> });
            }
            if (hasPermission(user, 'clientes:view')) {
                documentosSubItems.push({ to: "/documentos/anamneses", label: "Anamneses", icon: <ClipboardList size={18} /> });
            }

            if (documentosSubItems.length > 0) {
                menu.push({
                    type: 'submenu',
                    label: 'Documentos',
                    icon: <FileSignature size={18} />,
                    subItems: documentosSubItems
                });
            }
        }

        // 8. MENU TEMPLATES (matriz, franquia ou conta)
        if (isMatrizUser || isFranquiaUser || isContaUser) {
            const templatesSubItems = [
                { to: "/templates/contrato", label: "Contratos", icon: <FileText size={18} /> },
                { to: "/templates/anamnese", label: "Anamnese", icon: <ClipboardList size={18} /> },
                { to: "/templates/mensagem", label: "Mensagens", icon: <MessageSquare size={18} /> },
                { to: "/templates/recibo", label: "Recibos", icon: <Receipt size={18} /> },
                { to: "/templates/certificado", label: "Certificados", icon: <Award size={18} /> },
            ];
            
            // Tipos de Contrato
            if (hasPermission(user, 'tipos_contrato:manage')) {
                templatesSubItems.push({ 
                    to: "/cadastros/tipos-contrato", 
                    label: "Tipos de Contrato", 
                    icon: <FileSignature size={18} /> 
                });
            }
            
            if (templatesSubItems.length > 0) {
                menu.push({ 
                    type: 'submenu', 
                    label: 'Templates', 
                    icon: <ClipboardList size={18} />, 
                    subItems: templatesSubItems 
                });
            }
        }

        // 9. MENU INDICADORES
        if (hasPermission(user, 'indicadores:view')) {
            menu.push({ 
                type: 'submenu', 
                label: "Indicadores", 
                icon: <BarChart2 size={18} />, 
                subItems: [
                    { to: "/indicadores/faturamento", label: "Faturamento", icon: <DollarSign size={18} /> },
                    { to: "/indicadores/clientes", label: "Clientes", icon: <Users size={18} /> },
                    { to: "/indicadores/servicos", label: "Serviços", icon: <ClipboardList size={18} /> },
                    { to: "/indicadores/agendamentos", label: "Agendamentos", icon: <CalendarClock size={18} /> },
                    { to: "/indicadores/pagamentos", label: "Formas de Pagamento", icon: <CreditCard size={18} /> },
                    { to: "/indicadores/aniversariantes", label: "Aniversariantes", icon: <Cake size={18} /> },
                    { to: "/indicadores/comissoes", label: "Comissões", icon: <Percent size={18} /> },
                    { to: "/indicadores/produtos", label: "Produtos", icon: <ShoppingCart size={18} /> },
                ]
            });
        }

        // 10. SITE DA FRANQUIA / COMUNICAÇÃO (ex-"Site da Conta" — para conta
        // passou a reunir também os itens de comunicação que antes ficavam
        // em Configurações, mais um atalho para Templates > Mensagens) —
        // não exibe para Matriz (ela tem seu próprio bloco)
        if (!isMatrizUser && (isFranquiaUser || isContaUser)) {
            const siteComunicacaoSubItems = [
                { to: "/site-institucional/banners-home",       label: "Banners",            icon: <Image size={18} /> },
                { to: "/site-institucional/conteudos-home",     label: "Conteúdos Seções",   icon: <ListTree size={18} /> },
                { to: "/site-institucional/faq",                label: "FAQ",                icon: <HelpCircle size={18} /> },
                { to: "/site-institucional/redes-sociais",      label: "Redes Sociais",      icon: <Share2 size={18} /> },
                { to: "/site-institucional/navegacao",          label: "Navegação",          icon: <Compass size={18} /> },
                { to: "/site-institucional/mensagens",          label: "Mensagens de Contato", icon: <Mail size={18} /> },
                { to: "/site-institucional/configuracoes-home", label: "Configurações Home", icon: <Settings size={18} /> },
            ];

            if (isContaUser) {
                if (user.account) {
                    siteComunicacaoSubItems.push({ to: "/configuracoes/comunicacao/manual", label: "Comunicação Manual", icon: <MessageCircle size={18} /> });
                    siteComunicacaoSubItems.push({ to: "/configuracoes/comunicacao/automatica", label: "Comunicação Automática", icon: <MessageSquare size={18} /> });
                    if (hasPermission(user, 'agendamentos_comunicacao:manage')) {
                        siteComunicacaoSubItems.push({ to: "/configuracoes/comunicacao/agendadas", label: "Fila de Envio", icon: <Clock size={18} /> });
                    }
                }
                if (hasPermission(user, 'notificacoes:manage')) {
                    siteComunicacaoSubItems.push({ to: "/configuracoes/notificacoes", label: "Notificações", icon: <Bell size={18} /> });
                }
            }

            menu.push({
                type: 'submenu',
                label: isFranquiaUser ? 'Site da Franquia' : 'Comunicação',
                icon: <Globe size={18} />,
                subItems: siteComunicacaoSubItems
            });
        }

        if (!isMatrizUser && (isFranquiaUser || isContaUser)) {
            const gestaoContaSubItems = [];
            
            if (isContaUser && user.account) {
                // ✅ USUÁRIOS (antiga Equipe)
                if (hasPermission(user, 'equipe:view')) {
                    gestaoContaSubItems.push({ 
                        to: "/minha-conta/usuarios", 
                        label: "Usuários",  // ✅ ALTERADO DE "Equipe" PARA "Usuários"
                        icon: <UsersRound size={18} /> 
                    });
                }
                
                // ✅ PROFISSIONAIS - NOVO
                if (hasPermission(user, 'equipe:view') && hasFeature('hasServicos')) {
                    gestaoContaSubItems.push({
                        to: "/minha-conta/profissionais",
                        label: "Profissionais",
                        icon: <BriefcaseBusiness size={18} />
                    });
                }

                // ✅ UNIDADES - NOVO
                if (hasPermission(user, 'unidades:manage') && hasFeature('hasUnidades')) {
                    gestaoContaSubItems.push({
                        to: "/minha-conta/unidades",
                        label: "Unidades",
                        icon: <MapPin size={18} />
                    });
                }
                
                // ✅ CONFIGURAÇÕES DA CONTA
                if (hasPermission(user, 'configuracoes_conta:view')) {
                    gestaoContaSubItems.push({ 
                        to: "/minha-conta/config", 
                        label: "Configurações",
                        icon: <Settings size={18} /> 
                    });
                    gestaoContaSubItems.push({ 
                        to: "/minha-conta/pagina-publica", 
                        label: "Página Pública", 
                        icon: <Globe size={18} /> 
                    });
                }
                
                if (hasPermission(user, SUBSCRIPTION_PERMISSIONS.CONTA)) {
                    gestaoContaSubItems.push({ 
                        to: "/assinatura", 
                        label: "Meu Plano", 
                        icon: <CreditCard size={18} /> 
                    });
                }
            }
            
            if (user.account) {
                gestaoContaSubItems.push({ 
                    to: "/minha-conta/depoimentos-da-conta", 
                    label: "Depoimentos da Conta", 
                    icon: <MessageSquareQuote size={18} /> 
                });
            }
            
            if (gestaoContaSubItems.length > 0) {
                menu.push({ 
                    type: 'submenu', 
                    label: 'Minha Conta', 
                    icon: <Building size={18}/>, 
                    subItems: gestaoContaSubItems 
                });
            }
        }

        // 11. MENU MEU PERFIL (todos os usuários autenticados)
        const meuPerfilSubItems = [];
        meuPerfilSubItems.push({ 
            to: "/meu-perfil/minha-conta", 
            label: "Meus Dados", 
            icon: <UserIcon size={18} /> 
        });
        
        if (!isMatrizUser && !isCliente) {
            meuPerfilSubItems.push({ 
                to: "/minha-conta/depoimentos", 
                label: "Meus Depoimentos", 
                icon: <MessageSquareQuote size={18} /> 
            });
        }
        
        if (hasPermission(user, 'notificacoes:view')) {
            meuPerfilSubItems.push({ 
                to: "/meu-perfil/notificacoes", 
                label: "Minhas Notificações", 
                icon: <Bell size={18} /> 
            });
        }
        
        if (meuPerfilSubItems.length > 0) {
            menu.push({ 
                type: 'submenu', 
                label: 'Meu Perfil', 
                icon: <UserIcon size={18} />, 
                subItems: meuPerfilSubItems 
            });
        }

        // 12. MENU AUDITORIA/LOGS (exclusivo matriz — logs de sistema não
        // fazem sentido pro escopo de uma franquia individual)
        if (isMatrizUser) {
            const auditoriaSubItems = [];
            
            if (hasPermission(user, 'auditoria:view')) {
                auditoriaSubItems.push({ 
                    to: "/auditoria/logs", 
                    label: "Logs do Sistema", 
                    icon: <Database size={18} /> 
                });
            }
            
            if (hasPermission(user, 'auditoria:view')) {
                auditoriaSubItems.push({ 
                    to: "/auditoria/atividades", 
                    label: "Atividades", 
                    icon: <BarChart3 size={18} /> 
                });
            }
            
            if (auditoriaSubItems.length > 0) {
                menu.push({ 
                    type: 'submenu', 
                    label: 'Auditoria', 
                    icon: <Shield size={18} />, 
                    subItems: auditoriaSubItems 
                });
            }
        }

        // Log apenas uma vez por sessão
        const menuLogKey = `sidebar_menu_count_${user.email}`;
        if (process.env.NODE_ENV === 'development' && !sessionStorage.getItem(menuLogKey)) {
            console.log(`📊 Sidebar: ${menu.length} itens de menu gerados para ${user.email}`);
            sessionStorage.setItem(menuLogKey, 'true');
        }

        return menu;

    }, [user, isLoading, hasFeature]);

    // --- Renderização do Sidebar ---
    return (
        <aside className={`sidebar ${isOpen ? 'is-open' : ''} ${isCollapsed ? 'is-collapsed' : ''}`}>
            {/* Logo */}
            <div className="sidebar-logo-container">
                <Link to="/" onClick={toggleSidebar} className="sidebar-logo-link">
                    {branding?.logoDarkUrl ? (
                        <img src={branding.logoDarkUrl} alt="Logo" className="sidebar-logo-image" />
                    ) : (
                        <>
                            <img src="/images/brand/logo-mark.png" alt="SituAI" width={28} height={28} className="sidebar-logo-icon" />
                            <span className="sidebar-logo-text">Situ<span className="sidebar-logo-text-pro">AI</span></span>
                        </>
                    )}
                </Link>

                {/* Badge de scope */}
                {user && !isCollapsed && (
                    <div className="sidebar-scope-badge">
                        <span className={`scope-badge scope-${getUserScope(user)}`}>
                            {getUserScope(user)?.toUpperCase()}
                        </span>
                    </div>
                )}
            </div>

            {/* Navegação */}
            <nav className="sidebar-nav">
                <ul className="sidebar-nav-list">
                    {navItems.map((item, index) => {
                        if (item.type === 'submenu') {
                            return <SubMenu key={`${item.label}-${index}`} item={item} toggleSidebar={toggleSidebar}/>;
                        }
                        return <NavLink key={item.to || index} {...item} toggleSidebar={toggleSidebar} />;
                    })}
                </ul>
            </nav>

            {/* Rodapé */}
            {user && (
                <div className="sidebar-footer">
                    <div className="sidebar-user-details">
                        <img
                            src={user.photoUrl || `https://ui-avatars.com/api/?name=${encodeURIComponent(user.name || 'User')}&background=random`}
                            alt="Avatar"
                            className="sidebar-user-avatar"
                        />
                        <div className="sidebar-user-info">
                            <p className="sidebar-user-name" title={user.name}>
                                {user.name || 'Usuário'}
                            </p>
                            <p className="sidebar-user-account" title={user.account?.name || user.franquia?.nome || 'Matriz'}>
                                {user.account ? `Conta: ${user.account.name}` : 
                                 user.franquia ? `Franquia: ${user.franquia.nome}` : 
                                 'Matriz'}
                            </p>
                            <p className="sidebar-user-permissions">
                                Permissões: {user.role?.permissions?.length || 0}
                            </p>
                        </div>
                    </div>
                    <div className="sidebar-footer-actions">
                        <button onClick={onLogout} className="sidebar-nav-link sidebar-logout-button">
                            <LogOut size={18} className="sidebar-nav-icon" />
                            <span>Sair</span>
                        </button>

                        {/* Botão de retrair (só aparece em telas ≥1024px via CSS) */}
                        <button
                            type="button"
                            className="sidebar-collapse-toggle"
                            onClick={toggleCollapse}
                            aria-label={isCollapsed ? 'Expandir menu' : 'Retrair menu'}
                            title={isCollapsed ? 'Expandir menu' : 'Retrair menu'}
                        >
                            {isCollapsed ? <PanelLeftOpen size={18} /> : <PanelLeftClose size={18} />}
                        </button>
                    </div>
                    {!isCollapsed && <p className="sidebar-version">SituAI v{packageJson.version}</p>}
                </div>
            )}
            
            {/* Indicador de Loading */}
            {isLoading && !user && (
                <div className="sidebar-loading">
                    <Loader2 size={24} className="animate-spin" />
                    <span>Carregando...</span>
                </div>
            )}
            
            {/* Debug info (apenas em desenvolvimento) */}
            {process.env.NODE_ENV === 'development' && user && (
                <div className="sidebar-debug-info">
                    <details>
                        <summary>Debug Info</summary>
                        <pre className="text-xs p-2 bg-gray-800 text-white rounded">
                            {JSON.stringify(debugPermissions(user), null, 2)}
                        </pre>
                    </details>
                </div>
            )}
        </aside>
    );
};

export default Sidebar;